import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Mail, Package, ShoppingCart, X, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { notificationsAPI } from '../services/api';
import { playNotificationAlert, unlockNotificationSound } from '../utils/notificationSound';

const POLL_INTERVAL_MS = 4000;

const TYPE_META = {
  message: {
    label: 'Yeni Mesaj',
    icon: Mail,
    badgeClass: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  order: {
    label: 'Siparis Bildirimi',
    icon: ShoppingCart,
    badgeClass: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  },
  production: {
    label: 'Uretim Bildirimi',
    icon: Package,
    badgeClass: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  },
};

const getTypeMeta = (type) => TYPE_META[type] || {
  label: 'Yeni Bildirim',
  icon: Bell,
  badgeClass: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const NotificationAlertModal = ({ notification, isDark, onClose, onOpenLink }) => {
  const meta = getTypeMeta(notification.type);
  const Icon = meta.icon;

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 p-4 sm:p-6 animate-in fade-in duration-200"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`relative w-full max-w-lg overflow-hidden rounded-2xl shadow-2xl ${
          isDark ? 'border border-gray-700 bg-gray-900 text-white' : 'border border-slate-200 bg-white text-gray-900'
        }`}
        onClick={(event) => event.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="notification-alert-title"
      >
        <div className={`flex items-start gap-4 border-b p-5 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-slate-100 bg-slate-50'}`}>
          <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${meta.badgeClass}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold uppercase tracking-wide text-blue-600 dark:text-blue-400">
              {meta.label}
            </p>
            <h2 id="notification-alert-title" className="mt-1 text-lg font-bold leading-snug">
              {notification.title}
            </h2>
            <p className="mt-1 text-xs text-slate-500 dark:text-gray-400">
              {new Date(notification.created_at).toLocaleString('tr-TR')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-lg p-2 transition-colors ${
              isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-slate-400 hover:bg-slate-200'
            }`}
            aria-label="Uyariyi kapat"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700 dark:text-gray-200">
            {notification.message}
          </p>
        </div>

        <div className={`flex flex-wrap gap-3 border-t p-5 ${isDark ? 'border-gray-700' : 'border-slate-100'}`}>
          {notification.link && (
            <button
              type="button"
              onClick={onOpenLink}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              <ExternalLink className="h-4 w-4" />
              {notification.type === 'message' ? 'Mesajlari Ac' : 'Detaya Git'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className={`inline-flex flex-1 items-center justify-center rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
              isDark
                ? 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

const NotificationAlertManager = () => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const knownIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const [currentAlert, setCurrentAlert] = useState(null);
  const [alertQueue, setAlertQueue] = useState([]);

  const handleCloseAlert = useCallback(() => {
    setCurrentAlert(null);
  }, []);

  useEffect(() => {
    if (!currentAlert && alertQueue.length > 0) {
      const [next, ...rest] = alertQueue;
      setCurrentAlert(next);
      setAlertQueue(rest);
    }
  }, [currentAlert, alertQueue]);

  useEffect(() => {
    if (!currentAlert) {
      return undefined;
    }

    playNotificationAlert();
  }, [currentAlert?.id]);

  useEffect(() => {
    const unlock = () => unlockNotificationSound();
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => {
      document.removeEventListener('click', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  const checkNotifications = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      const response = await notificationsAPI.getNotifications();
      const items = response.data || [];
      const unread = items.filter((item) => !item.is_read);
      const freshAlerts = unread.filter((item) => !knownIdsRef.current.has(item.id));

      items.forEach((item) => knownIdsRef.current.add(item.id));

      if (!initializedRef.current) {
        initializedRef.current = true;
        window.dispatchEvent(new CustomEvent('roysis:notifications-refresh'));
        return;
      }

      if (freshAlerts.length === 0) {
        return;
      }

      const sorted = [...freshAlerts].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );

      setAlertQueue((queue) => [...queue, ...sorted]);
      window.dispatchEvent(new CustomEvent('roysis:notifications-refresh'));
    } catch (error) {
      console.error('Anlik bildirim kontrolu basarisiz:', error);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      knownIdsRef.current = new Set();
      initializedRef.current = false;
      setCurrentAlert(null);
      setAlertQueue([]);
      return undefined;
    }

    checkNotifications();
    const interval = setInterval(checkNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, checkNotifications]);

  const handleOpenLink = useCallback(async () => {
    if (!currentAlert) {
      return;
    }

    try {
      await notificationsAPI.markAsRead(currentAlert.id);
      knownIdsRef.current.add(currentAlert.id);
      window.dispatchEvent(new CustomEvent('roysis:notifications-refresh'));
    } catch (error) {
      console.error('Bildirim okundu isaretlenemedi:', error);
    }

    const link = currentAlert.link;
    setCurrentAlert(null);

    if (link) {
      navigate(link.startsWith('/') ? link : `/${link}`);
    }
  }, [currentAlert, navigate]);

  if (!user || !currentAlert) {
    return null;
  }

  return (
    <NotificationAlertModal
      notification={currentAlert}
      isDark={isDark}
      onClose={handleCloseAlert}
      onOpenLink={handleOpenLink}
    />
  );
};

export default NotificationAlertManager;
